import { useEffect, useState } from 'react';
import { useInitializationDataContext } from '../../../../context/data-context';
import { useActiveCanvas } from '../../../../hooks/use-active-canvas';
import { convertPixelToPoint, convertPointToPixel, getGroupedTextObject } from '../../../../utils';
import { AlignmentValue } from '../../alignment-control';
import { Font, FontSelectedOption } from '../../font-drawer/font-drawer-types';
import { ALIGNMENT_OPTIONS } from '../../font-drawer/utils/constants';
import { findKeyByValue } from '../../font-drawer/utils/find-key-by-value';
import { formatFontCollection } from '../../font-drawer/utils/format-font-collection';
import { getLabelByFontId } from '../../font-drawer/utils/get-label-by-fontid';
import { useHistoryTransaction } from '../../../../features/edit-history/edit-history';

const INDEX_TO_ALIGNMENT: Record<number, AlignmentValue> = { 0: 'left', 1: 'center', 2: 'right' };

/**
 * Reads initial font values from the active canvas text object and returns
 * onChange handlers that write back to the canvas. Mirrors the logic inside
 * font-tab.tsx so the FontToolbar controls behave identically to the legacy
 * FontDrawer.
 */
export const useFontToolbarCanvas = () => {
  const canvas = useActiveCanvas();
  const runHistoryTransaction = useHistoryTransaction();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const textObject = getGroupedTextObject(canvas?.current) as fabric.Textbox | null;

  const [fonts, setFonts] = useState<Font[]>([]);

  const initialFontSize = textObject?.fontSize ? convertPixelToPoint(textObject.fontSize) : 30;
  const initialFontFamily = textObject?.fontFamily
    ? getLabelByFontId(textObject.fontFamily, initializedData?.font_collection.fonts)
    : '';

  const alignmentIndex = findKeyByValue(textObject?.textAlign);
  // eslint-disable-next-line security/detect-object-injection
  const initialAlignment: AlignmentValue = INDEX_TO_ALIGNMENT[alignmentIndex] ?? 'left';

  useEffect(() => {
    const collection = formatFontCollection(initializedData?.font_collection.fonts);
    setFonts(collection);
  }, [initializedData?.font_collection.fonts]);

  const onFontFamilyChange = (selected: FontSelectedOption) => {
    const font = fonts.find((f: Font) => f.id === selected.id);
    const targetCanvas = canvas?.current;
    if (font && targetCanvas) {
      void runHistoryTransaction(targetCanvas, () => {
        const activeText = getGroupedTextObject(targetCanvas) as fabric.Textbox | null;
        if (!activeText) return;
        activeText.setOptions({ fontFamily: font.family });
        targetCanvas.requestRenderAll();
      });
    }
  };

  const onFontSizeChange = (size: number) => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;
    const fabricFontSize = convertPointToPixel(size);
    void runHistoryTransaction(targetCanvas, () => {
      const activeText = getGroupedTextObject(targetCanvas) as fabric.Textbox | null;
      if (!activeText) return;
      activeText.setOptions({ fontSize: fabricFontSize });
      targetCanvas.requestRenderAll();
    });
  };

  const onAlignmentChange = (alignment: AlignmentValue) => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;
    const entries = Object.entries(ALIGNMENT_OPTIONS);
    const index = entries.findIndex(([, v]) => v === alignment);
    if (index !== -1) {
      // eslint-disable-next-line security/detect-object-injection
      const textAlignment = ALIGNMENT_OPTIONS[index.toString()] ?? 'left';
      void runHistoryTransaction(targetCanvas, () => {
        const activeText = getGroupedTextObject(targetCanvas) as fabric.Textbox | null;
        if (!activeText) return;
        activeText.setOptions({ textAlign: textAlignment });
        targetCanvas.requestRenderAll();
      });
    }
  };

  return {
    fonts,
    initialFontSize,
    initialFontFamily,
    initialAlignment,
    onFontFamilyChange,
    onFontSizeChange,
    onAlignmentChange,
  };
};
